import {
  DownOutlined,
  FolderOpenFilled,
  FolderOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Spin, Tree } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import slugify from 'slugify';

const MarketplaceCategoriesTree = ({
  categories = [],
  onSelect,
  loading = false,
}) => {
  const { category: categoryName } = useParams();

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Transformer les données de catégories au format Tree d'Ant Design
  const treeData = useMemo(() => {
    if (!categories || !Array.isArray(categories)) return [];

    const transformCategory = (category) => {
      const treeNode = {
        title: category.label || category.name,
        key: category.code || category.id?.toString(),
        id: category.id,
        code: category.name,
        isLeaf: !category.children || category.children.length === 0,
        icon:
          !category.children || category.children.length === 0 ? (
            <FolderOutlined style={{ color: '#22247B' }} />
          ) : (
            <FolderOpenFilled style={{ color: '#22247B' }} />
          ),
      };

      // Si la catégorie a des enfants, les transformer récursivement
      if (category.children && category.children.length > 0) {
        treeNode.children = category.children.map(transformCategory);
      }

      return treeNode;
    };

    return categories.map(transformCategory);
  }, [categories]);

  // Générer toutes les clés pour l'expansion par défaut
  const getAllKeys = useMemo(() => {
    const keys = [];

    const extractKeys = (nodes) => {
      nodes.forEach((node) => {
        keys.push(node.key);
        if (node.children && node.children.length > 0) {
          extractKeys(node.children);
        }
      });
    };

    if (treeData && treeData.length > 0) {
      extractKeys(treeData);
    }

    return keys;
  }, [treeData]);

  // Définir les clés expandues par défaut quand les données changent
  useEffect(() => {
    if (getAllKeys.length > 0) {
      setExpandedKeys(getAllKeys);
    }
  }, [getAllKeys]);

  const handleSelect = (selectedKeys, info) => {
    setSelectedKeys(selectedKeys);
    if (onSelect) {
      const category =
        categories.filter((c) => c.id === info?.node?.id)[0] || null;
      onSelect(category, info);
    }
  };

  const handleExpand = (expandedKeys, info) => {
    setExpandedKeys(expandedKeys);
    console.log('Expanded keys:', expandedKeys, info);
  };

  // Reset selected keys when categories change
  useEffect(() => {
    const findMatchingKey = (nodes) => {
      const categorySlug = slugify(categoryName || '', {
        lower: true,
        strict: true,
      });
      for (const node of nodes) {
        const nodeTitleSlug = slugify(node.title || '', {
          lower: true,
          strict: true,
        });
        if (nodeTitleSlug === categorySlug) {
          return node.key;
        }
        if (node.children?.length) {
          const childKey = findMatchingKey(node.children);
          if (childKey) return childKey;
        }
      }
      return null;
    };

    const key = findMatchingKey(treeData);
    setSelectedKeys(key ? [key] : []);
  }, [treeData, categoryName]);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin indicator={<LoadingOutlined spin />} />
        <span style={{ marginLeft: '10px' }}>Chargement des catégories...</span>
      </div>
    );
  }

  if (!treeData || treeData.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
        Aucune catégorie trouvée
      </div>
    );
  }

  return (
    <div style={{ overflowY: 'auto' }}>
      <Tree
        treeData={treeData}
        onSelect={handleSelect}
        onExpand={handleExpand}
        switcherIcon={<DownOutlined />}
        selectedKeys={selectedKeys}
        expandedKeys={expandedKeys}
        showLine={{ showLeafIcon: false }}
        showIcon={true}
        selectable={true}
        multiple={false}
        blockNode={false}
        style={{
          backgroundColor: 'transparent',
          fontSize: '14px',
        }}
      />
    </div>
  );
};

export default MarketplaceCategoriesTree;
